import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Pencil, Plus, Search, ShoppingCart, Store, Trash2, Upload } from 'lucide-react';
import AppPageIntro from '@/components/AppPageIntro';
import { apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

const DEFAULT_STORES = ['Jumbo', 'Lidl', 'Albert Heijn'];
const GROCERY_UNITS = ['gram', 'kg', 'ml', 'liter', 'stuks'];

const selectionChipClass =
  'h-9 rounded-full px-3 text-sm font-medium shadow-none';

const emptyVariantForm = {
  productnaam: '',
  categorie: '',
  merknaam: '',
  isHuismerk: false,
  hoeveelheid: '1',
  eenheid: 'stuks',
  nutriScore: '',
  winkel: 'Jumbo',
  prijs: '',
};

const variantLabel = (variant) =>
  `${variant.merknaam} · ${variant.hoeveelheid} ${variant.eenheid}${variant.isHuismerk ? ' · huismerk' : ''}`;

const listVariantLabel = (variant) => `${variant.productNaam} · ${variantLabel(variant)}`;

function SearchableVariantSelect({ variants, value, onChange }) {
  const [open, setOpen] = useState(false);

  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.id === value),
    [variants, value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-14 w-full justify-between rounded-2xl border-border/70 bg-background px-4 text-left font-normal"
        >
          <span className="truncate">
            {selectedVariant ? listVariantLabel(selectedVariant) : 'Zoek product'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        className="w-[min(92vw,34rem)] rounded-2xl border-border/70 p-0 shadow-xl"
      >
        <Command>
          <CommandInput placeholder="Zoek op product, merk of inhoud..." />
          <CommandList className="max-h-[45svh]">
            <CommandEmpty>Geen product gevonden.</CommandEmpty>
            <CommandGroup>
              {variants.map((variant) => (
                <CommandItem
                  key={variant.id}
                  value={`${variant.productNaam} ${variant.merknaam} ${variant.hoeveelheid} ${variant.eenheid} ${variant.categorie || ''}`}
                  onSelect={() => {
                    onChange(variant.id);
                    setOpen(false);
                  }}
                  className="items-start py-3"
                >
                  <Check className={`mr-2 mt-0.5 h-4 w-4 shrink-0 ${value === variant.id ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{variant.productNaam}</div>
                    <div className="truncate text-xs text-muted-foreground">{variantLabel(variant)}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Groceries() {
  const [loading, setLoading] = useState(true);
  const [savingVariant, setSavingVariant] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [shoppingList, setShoppingList] = useState({ id: '', naam: 'Boodschappenlijst', items: [] });
  const [comparison, setComparison] = useState({ vergelijkingen: [] });
  const [stores, setStores] = useState(DEFAULT_STORES);
  const [selectedStores, setSelectedStores] = useState(DEFAULT_STORES);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvImportResult, setCsvImportResult] = useState(null);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState('all');
  const [showProductForm, setShowProductForm] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const [variantForm, setVariantForm] = useState(emptyVariantForm);

  const loadGroceries = async () => {
    try {
      setLoading(true);
      const catalogRes = await apiClient.get('/groceries/catalog');
      const nextProducts = Array.isArray(catalogRes.data?.products) ? catalogRes.data.products : [];
      const nextStores = Array.isArray(catalogRes.data?.stores) && catalogRes.data.stores.length > 0
        ? catalogRes.data.stores
        : DEFAULT_STORES;
      const nextList = catalogRes.data?.shoppingList || { id: '', naam: 'Boodschappenlijst', items: [] };

      setCatalog(nextProducts);
      setStores(nextStores);
      setSelectedStores((prev) => (prev.length > 0 ? prev : nextStores));
      setShoppingList(nextList);

      const firstVariant = nextProducts.flatMap((product) => product.varianten || [])[0];
      if (firstVariant) {
        setSelectedVariantId((prev) => prev || firstVariant.id);
      }
    } catch (error) {
      console.error('Error loading groceries MVP:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async (storeNames = selectedStores, listPayload = shoppingList) => {
    try {
      const storesParam = storeNames.join(',');
      const listToUse = listPayload?.id
        ? listPayload
        : (await apiClient.get('/groceries/shopping-list')).data;
      if (!listToUse?.id) {
        setComparison({ vergelijkingen: [] });
        return;
      }

      const res = await apiClient.get('/groceries/store-comparison', {
        params: { stores: storesParam },
      });
      setComparison(res.data || { vergelijkingen: [] });
    } catch (error) {
      console.error('Error loading grocery comparison:', error);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      await loadGroceries();
    };
    bootstrap();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsCompactViewport(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchComparison(selectedStores);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const allVariants = useMemo(
    () =>
      catalog.flatMap((product) =>
        (product.varianten || []).map((variant) => ({
          ...variant,
          productNaam: product.naam,
          categorie: product.categorie,
        }))
      ),
    [catalog]
  );

  const variantIndex = useMemo(() => {
    const map = new Map();
    allVariants.forEach((variant) => map.set(variant.id, variant));
    return map;
  }, [allVariants]);

  const listRows = useMemo(
    () =>
      (shoppingList.items || []).map((item) => ({
        ...item,
        variant: variantIndex.get(item.productVariantId),
      })),
    [shoppingList.items, variantIndex]
  );

  const catalogCategories = useMemo(() => {
    const categories = Array.from(
      new Set(
        catalog
          .map((product) => product.categorie)
          .filter(Boolean)
      )
    );
    return categories.sort((a, b) => a.localeCompare(b, 'nl'));
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    const query = catalogSearch.trim().toLowerCase();

    return catalog.filter((product) => {
      const categoryMatches = catalogCategory === 'all' || product.categorie === catalogCategory;
      if (!categoryMatches) return false;
      if (!query) return true;

      const haystacks = [
        product.naam,
        product.categorie,
        ...(product.varianten || []).flatMap((variant) => [
          variant.merknaam,
          variant.eenheid,
          variant.nutriScore,
          ...(variant.prijzen || []).map((price) => price.winkelnaam),
        ]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystacks.includes(query);
    });
  }, [catalog, catalogCategory, catalogSearch]);

  const catalogResults = useMemo(() => filteredCatalog.slice(0, 12), [filteredCatalog]);
  const shouldShowCatalogResults = catalogSearch.trim().length > 0 || catalogCategory !== 'all';
  const csvImportErrors = csvImportResult?.errors || [];

  const handleVariantFormChange = (key, value) => {
    setVariantForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetVariantEditor = () => {
    setEditingVariantId(null);
    setEditingPriceId(null);
    setVariantForm(emptyVariantForm);
    setShowProductForm(false);
  };

  const startEditingVariant = (product, variant, price = null) => {
    setEditingVariantId(variant.id);
    setEditingPriceId(price?.id || null);
    setVariantForm({
      productnaam: product.naam || '',
      categorie: product.categorie || '',
      merknaam: variant.merknaam || '',
      isHuismerk: Boolean(variant.isHuismerk),
      hoeveelheid: String(variant.hoeveelheid ?? '1'),
      eenheid: variant.eenheid || 'stuks',
      nutriScore: variant.nutriScore || '',
      winkel: price?.winkelnaam || variant.standaardWinkelnaam || stores[0] || 'Jumbo',
      prijs: price?.prijs != null ? String(price.prijs) : '',
    });
    setShowProductForm(true);
  };

  const saveVariant = async () => {
    if (
      !variantForm.productnaam.trim() ||
      !variantForm.categorie.trim() ||
      !variantForm.merknaam.trim() ||
      Number(variantForm.hoeveelheid) <= 0 ||
      Number(variantForm.prijs) <= 0
    ) {
      return;
    }

    try {
      setSavingVariant(true);
      const payload = {
        productnaam: variantForm.productnaam.trim(),
        categorie: variantForm.categorie.trim(),
        merknaam: variantForm.merknaam.trim(),
        isHuismerk: variantForm.isHuismerk,
        hoeveelheid: Number(variantForm.hoeveelheid),
        eenheid: variantForm.eenheid,
        nutriScore: variantForm.nutriScore.trim() || null,
        winkel: variantForm.winkel,
        prijs: Number(variantForm.prijs),
      };

      if (editingVariantId) {
        await apiClient.put(`/groceries/variants/${editingVariantId}`, payload);
        if (editingPriceId) {
          await apiClient.put(`/groceries/prices/${editingPriceId}`, {
            winkel: variantForm.winkel,
            prijs: Number(variantForm.prijs),
          });
        } else {
          await apiClient.post('/groceries/variants', payload);
        }
      } else {
        await apiClient.post('/groceries/variants', payload);
      }

      resetVariantEditor();
      await loadGroceries();
    } catch (error) {
      console.error('Error saving product variant:', error);
    } finally {
      setSavingVariant(false);
    }
  };

  const addVariantToList = () => {
    if (!selectedVariantId) return;
    setShoppingList((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        {
          id: crypto.randomUUID(),
          productVariantId: selectedVariantId,
          aantal: 1,
        },
      ],
    }));
  };

  const updateListItem = (itemId, patch) => {
    setShoppingList((prev) => ({
      ...prev,
      items: (prev.items || []).map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
    }));
  };

  const removeListItem = (itemId) => {
    setShoppingList((prev) => ({
      ...prev,
      items: (prev.items || []).filter((item) => item.id !== itemId),
    }));
  };

  const saveShoppingList = async () => {
    try {
      setSavingList(true);
      const payload = {
        naam: shoppingList.naam,
        items: (shoppingList.items || []).map((item) => ({
          productVariantId: item.productVariantId,
          aantal: Number(item.aantal || 1),
        })),
      };
      const res = await apiClient.put('/groceries/shopping-list', payload);
      setShoppingList(res.data || shoppingList);
      await fetchComparison(selectedStores, res.data || shoppingList);
    } catch (error) {
      console.error('Error saving grocery list:', error);
    } finally {
      setSavingList(false);
    }
  };

  const toggleStore = (storeName) => {
    setSelectedStores((prev) => {
      const exists = prev.includes(storeName);
      if (exists && prev.length === 1) return prev;
      const next = exists ? prev.filter((store) => store !== storeName) : [...prev, storeName];
      return next;
    });
  };

  const compareStores = async () => {
    await saveShoppingList();
  };

  const importCsv = async () => {
    if (!csvFile) return;

    try {
      setImportingCsv(true);
      const formData = new FormData();
      formData.append('file', csvFile);

      const res = await apiClient.post('/groceries/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setCsvImportResult(res.data || null);
      setCsvFile(null);
      await loadGroceries();
      await fetchComparison(selectedStores);
    } catch (error) {
      console.error('Error importing grocery CSV:', error);
      setCsvImportResult({
        rows_processed: 0,
        products_created: 0,
        variants_created: 0,
        variants_updated: 0,
        prices_inserted: 0,
        prices_updated: 0,
        skipped_rows: 1,
        errors: [error?.response?.data?.detail || 'CSV import is niet gelukt'],
      });
    } finally {
      setImportingCsv(false);
    }
  };

  const deleteVariant = async (variantId) => {
    const confirmed = window.confirm('Weet je zeker dat je deze productvariant wilt verwijderen? Bijbehorende winkelprijzen en lijstverwijzingen worden ook verwijderd.');
    if (!confirmed) return;

    try {
      await apiClient.delete(`/groceries/variants/${variantId}`);
      if (editingVariantId === variantId) {
        resetVariantEditor();
      }
      await loadGroceries();
      await fetchComparison(selectedStores);
    } catch (error) {
      console.error('Error deleting grocery variant:', error);
    }
  };

  const deletePrice = async (priceId) => {
    const confirmed = window.confirm('Weet je zeker dat je deze winkelprijs wilt verwijderen?');
    if (!confirmed) return;

    try {
      await apiClient.delete(`/groceries/prices/${priceId}`);
      if (editingPriceId === priceId) {
        setEditingPriceId(null);
        setVariantForm((prev) => ({ ...prev, prijs: '', winkel: stores[0] || 'Jumbo' }));
      }
      await loadGroceries();
      await fetchComparison(selectedStores);
    } catch (error) {
      console.error('Error deleting grocery price:', error);
    }
  };

  const productFormBody = (
    <div className="space-y-4 overflow-x-hidden">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Productnaam</Label>
          <Input value={variantForm.productnaam} onChange={(e) => handleVariantFormChange('productnaam', e.target.value)} placeholder="Bijv. Melk" />
        </div>
        <div className="space-y-2">
          <Label>Categorie</Label>
          <Input value={variantForm.categorie} onChange={(e) => handleVariantFormChange('categorie', e.target.value)} placeholder="Bijv. Zuivel" />
        </div>
        <div className="space-y-2">
          <Label>Merknaam</Label>
          <Input value={variantForm.merknaam} onChange={(e) => handleVariantFormChange('merknaam', e.target.value)} placeholder="Bijv. Campina" />
        </div>
        <div className="space-y-2">
          <Label>Nutri-Score optioneel</Label>
          <Input value={variantForm.nutriScore} onChange={(e) => handleVariantFormChange('nutriScore', e.target.value)} placeholder="A, B, C..." />
        </div>
        <div className="space-y-2">
          <Label>Hoeveelheid</Label>
          <Input type="number" min="0" step="0.01" value={variantForm.hoeveelheid} onChange={(e) => handleVariantFormChange('hoeveelheid', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Eenheid</Label>
          <div className="flex flex-wrap gap-2">
            {GROCERY_UNITS.map((unit) => {
              const active = variantForm.eenheid === unit;
              return (
                <Button
                  key={unit}
                  type="button"
                  variant={active ? 'default' : 'outline'}
                  className={selectionChipClass}
                  onClick={() => handleVariantFormChange('eenheid', unit)}
                >
                  {unit}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Winkel</Label>
          <div className="flex flex-wrap gap-2">
            {stores.map((store) => {
              const active = variantForm.winkel === store;
              return (
                <Button
                  key={store}
                  type="button"
                  variant={active ? 'default' : 'outline'}
                  className={selectionChipClass}
                  onClick={() => handleVariantFormChange('winkel', store)}
                >
                  {store}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Prijs</Label>
          <Input type="number" min="0" step="0.01" value={variantForm.prijs} onChange={(e) => handleVariantFormChange('prijs', e.target.value)} placeholder="2.49" />
        </div>
      </div>

      <label className="flex items-center gap-3 text-sm text-muted-foreground">
        <Checkbox checked={variantForm.isHuismerk} onCheckedChange={(checked) => handleVariantFormChange('isHuismerk', Boolean(checked))} />
        Huismerk
      </label>

      <div className="flex flex-wrap gap-3">
        <Button className="rounded-full" onClick={saveVariant} disabled={savingVariant}>
          {savingVariant ? 'Opslaan...' : editingVariantId ? 'Wijzigingen opslaan' : 'Product opslaan'}
        </Button>
        <Button type="button" variant="outline" className="rounded-full" onClick={resetVariantEditor}>
          Annuleren
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-56 rounded bg-muted" />
        <div className="h-56 rounded-3xl bg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="groceries-page">
      <AppPageIntro
        compact
        eyebrow="Boodschappen"
        title="Producten, lijst en winkelvergelijking"
        description="Zoek producten, zet ze op je lijst en vergelijk daarna direct het totaal per winkel."
        badges={['Zoeken', 'Lijst', 'Vergelijken']}
        icon={ShoppingCart}
      />

      {isCompactViewport ? (
        <Drawer open={showProductForm} onOpenChange={(open) => {
          setShowProductForm(open);
          if (!open) {
            setEditingVariantId(null);
            setEditingPriceId(null);
          }
        }}>
          <DrawerContent className="px-4 pb-4">
            <DrawerHeader className="px-0 text-left">
              <DrawerTitle>{editingVariantId ? 'Product bewerken' : 'Product toevoegen'}</DrawerTitle>
              <DrawerDescription>Houd de invoer compact. Variant en prijs volgen uit de velden hieronder.</DrawerDescription>
            </DrawerHeader>
            {productFormBody}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={showProductForm} onOpenChange={(open) => {
          setShowProductForm(open);
          if (!open) {
            setEditingVariantId(null);
            setEditingPriceId(null);
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingVariantId ? 'Product bewerken' : 'Product toevoegen'}</DialogTitle>
              <DialogDescription>Houd de invoer compact. Variant en prijs volgen uit de velden hieronder.</DialogDescription>
            </DialogHeader>
            {productFormBody}
          </DialogContent>
        </Dialog>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="shadow-card xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              CSV import
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload een CSV met kolommen zoals <code>supermarkt</code>, <code>categorie</code>, <code>productnaam</code>, <code>merk</code>, <code>inhoud</code>, <code>eenheid</code> en <code>prijs</code>. Bestaande varianten en winkelprijzen worden bijgewerkt waar mogelijk.
            </p>

            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              />
              <Button className="rounded-full" onClick={importCsv} disabled={!csvFile || importingCsv}>
                {importingCsv ? 'Importeren...' : 'CSV importeren'}
              </Button>
            </div>

            {csvImportResult && (
              <div className="rounded-2xl border bg-muted/20 p-4 space-y-3">
                <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Rijen verwerkt</p>
                    <p className="font-heading text-2xl font-bold">{csvImportResult.rows_processed ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Producten nieuw</p>
                    <p className="font-heading text-2xl font-bold">{csvImportResult.products_created ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Varianten nieuw</p>
                    <p className="font-heading text-2xl font-bold">{csvImportResult.variants_created ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Varianten update</p>
                    <p className="font-heading text-2xl font-bold">{csvImportResult.variants_updated ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Prijzen nieuw</p>
                    <p className="font-heading text-2xl font-bold">{csvImportResult.prices_inserted ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Prijzen update</p>
                    <p className="font-heading text-2xl font-bold">{csvImportResult.prices_updated ?? 0}</p>
                  </div>
                </div>

                {(csvImportResult.skipped_rows ?? 0) > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    {csvImportResult.skipped_rows} rijen zijn overgeslagen.
                  </div>
                )}

                {csvImportErrors.length > 0 && (
                  <div className="rounded-2xl border border-red-200 bg-red-50/80 p-4 text-sm text-red-800">
                    <p className="font-medium">
                      {csvImportErrors.length} rijen konden niet volledig worden verwerkt.
                    </p>
                    <p className="mt-1 text-red-700">
                      Controleer vooral inhoud-, hoeveelheid- en prijsvelden in je CSV.
                    </p>
                    <details className="mt-3">
                      <summary className="cursor-pointer font-medium text-red-800">
                        Bekijk details
                      </summary>
                      <div className="mt-3 space-y-2">
                        {csvImportErrors.map((errorLine) => (
                          <div key={errorLine} className="rounded-xl border border-red-200 bg-background/70 p-3 text-red-700">
                            {errorLine}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Product toevoegen
              </CardTitle>
              <Button type="button" className="rounded-full w-full sm:w-auto" onClick={() => {
                setEditingVariantId(null);
                setEditingPriceId(null);
                setVariantForm(emptyVariantForm);
                setShowProductForm(true);
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuw product
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Gebruik de knop hierboven om een product compact toe te voegen of te bewerken. Zo blijft deze pagina rustig en overzichtelijk.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Winkelvergelijking
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {stores.map((store) => {
                const active = selectedStores.includes(store);
                return (
                  <Button
                    key={store}
                    type="button"
                    variant={active ? 'default' : 'outline'}
                    className={selectionChipClass}
                    onClick={() => toggleStore(store)}
                  >
                    {store}
                  </Button>
                );
              })}
            </div>

            <Button className="rounded-full w-full sm:w-auto" onClick={compareStores} disabled={savingList}>
              {savingList ? 'Bijwerken...' : 'Lijst opslaan en vergelijken'}
            </Button>

            <div className="space-y-3">
              {(comparison.vergelijkingen || []).length === 0 && (
                <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
                  Nog geen vergelijking. Voeg eerst producten toe aan je lijst.
                </div>
              )}

              {(comparison.vergelijkingen || []).map((row) => (
                <div key={row.winkelnaam} className="rounded-2xl border bg-muted/20 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">{row.winkelnaam}</p>
                      <p className="font-heading text-2xl font-bold">€{Number(row.totaal || 0).toFixed(2)}</p>
                    </div>
                    <div className={`rounded-full px-3 py-1 text-xs font-medium ${row.volledig ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {row.volledig ? 'Volledig' : `${row.missendeItems.length} missen`}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {row.items.map((item) => (
                      <div key={`${row.winkelnaam}-${item.productVariantId}`} className="rounded-xl border bg-background p-3">
                        <p className="font-medium">{item.winkelProductNaam || item.productNaam}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.winkelProductNaam && item.winkelProductNaam !== item.productNaam
                            ? `${item.productNaam} · ${item.variantLabel}`
                            : item.variantLabel}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {item.aantal} × €{Number(item.prijsPerStuk).toFixed(2)} = €{Number(item.regelTotaal).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {row.missendeItems.length > 0 && (
                    <div className="mt-4 rounded-xl border border-dashed p-3 text-sm text-muted-foreground">
                      Mist prijs voor: {row.missendeItems.map((item) => `${item.productNaam} (${item.variantLabel})`).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Boodschappenlijst
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_140px_auto]">
            <div className="space-y-2">
              <Label>Product zoeken</Label>
              <SearchableVariantSelect
                variants={allVariants}
                value={selectedVariantId}
                onChange={setSelectedVariantId}
              />
            </div>
            <Input
              value={shoppingList.naam}
              onChange={(e) => setShoppingList((prev) => ({ ...prev, naam: e.target.value }))}
              placeholder="Lijstnaam"
              className="md:self-end"
            />
            <Button className="rounded-full md:self-end" onClick={addVariantToList} disabled={!selectedVariantId}>
              Aan lijst toevoegen
            </Button>
          </div>

          <div className="space-y-3">
            {listRows.length === 0 && (
              <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
                Nog geen producten op je lijst.
              </div>
            )}
            {listRows.map((row) => (
              <div key={row.id} className="rounded-2xl border bg-background p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-medium">{row.variant?.productNaam || 'Onbekend product'}</p>
                    <p className="text-sm text-muted-foreground">{row.variant ? variantLabel(row.variant) : 'Variant ontbreekt'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      className="w-24"
                      type="number"
                      min="0"
                      step="0.1"
                      value={row.aantal}
                      onChange={(e) => updateListItem(row.id, { aantal: Number(e.target.value || 0) })}
                    />
                    <Button variant="outline" size="icon" onClick={() => removeListItem(row.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Productdatabase
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
            <div className="space-y-2">
              <Label>Zoek product of merk</Label>
              <Input
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Bijv. melk, campina, jumbo..."
              />
            </div>
            <div className="space-y-2">
              <Label>Categorie</Label>
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
                <Button
                  type="button"
                  variant={catalogCategory === 'all' ? 'default' : 'outline'}
                  className={`${selectionChipClass} shrink-0`}
                  onClick={() => setCatalogCategory('all')}
                >
                  Alle categorieën
                </Button>
                {catalogCategories.map((category) => (
                  <Button
                    key={category}
                    type="button"
                    variant={catalogCategory === category ? 'default' : 'outline'}
                    className={`${selectionChipClass} shrink-0`}
                    onClick={() => setCatalogCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {catalog.length === 0 && (
            <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
              Nog geen producten. Voeg via de knop hierboven je eerste product toe.
            </div>
          )}

          {!shouldShowCatalogResults && catalog.length > 0 && (
            <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
              Zoek op product of kies eerst een categorie. Zo blijft de productdatabase compact en overzichtelijk.
            </div>
          )}

          {shouldShowCatalogResults && catalog.length > 0 && filteredCatalog.length === 0 && (
            <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
              Geen producten gevonden voor deze zoekopdracht of categorie.
            </div>
          )}

          {shouldShowCatalogResults && filteredCatalog.length > 12 && (
            <div className="rounded-2xl border bg-muted/20 p-4 text-sm text-muted-foreground">
              {filteredCatalog.length} resultaten gevonden. We tonen de eerste 12, zodat het overzichtelijk blijft.
            </div>
          )}

          {catalogResults.map((product) => (
            <div key={product.id} className="rounded-2xl border p-4 overflow-hidden">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <div>
                  <h3 className="font-heading text-xl font-semibold">{product.naam}</h3>
                  <p className="text-sm text-muted-foreground">{product.categorie}</p>
                </div>
                <p className="text-sm text-muted-foreground">{(product.varianten || []).length} varianten</p>
              </div>

              <div className="mt-4 space-y-3">
                {(product.varianten || []).map((variant) => (
                  <div key={variant.id} className="rounded-xl border bg-muted/20 p-3 overflow-hidden">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="font-medium">{variantLabel(variant)}</p>
                        {variant.nutriScore && (
                          <p className="text-sm text-muted-foreground">Nutri-Score: {variant.nutriScore}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full w-full sm:w-auto"
                        onClick={() => startEditingVariant(product, variant, (variant.prijzen || [])[0] || null)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Bewerk
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 overflow-hidden">
                      {(variant.prijzen || []).length === 0 && (
                        <span className="text-sm text-muted-foreground">Nog geen prijzen bekend</span>
                      )}
                      {(variant.prijzen || []).map((price) => (
                        <div key={price.id} className="inline-flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-1 text-xs text-muted-foreground">
                          <button
                            type="button"
                            className="truncate transition-colors hover:text-primary"
                            onClick={() => startEditingVariant(product, variant, price)}
                          >
                            {price.winkelnaam}: €{Number(price.prijs).toFixed(2)}
                          </button>
                          <button
                            type="button"
                            className="rounded-full p-1 text-muted-foreground transition-colors hover:text-destructive"
                            onClick={() => deletePrice(price.id)}
                            aria-label={`Verwijder prijs ${price.winkelnaam}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        className="rounded-full text-destructive hover:text-destructive"
                        onClick={() => deleteVariant(variant.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Verwijder variant
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
